import * as React from 'react';
import { api, applyQueryString, handleApiError } from 'services/api';
import type {
  ViagemBilling,
  ViagensBillingFilters,
  ViagensBillingPaginatedResponse,
  ViagensBillingPaginationControl,
  ViagensBillingViagensFilters,
  ViagensBillingViagensPaginatedResponse,
} from 'pages/viagens-controle/types';

type ListBillingsPayload = {
  filters?: ViagensBillingFilters;
  pagination?: ViagensBillingPaginationControl;
};

type ListBillableViagensPayload = {
  filters?: ViagensBillingViagensFilters;
  pagination?: ViagensBillingPaginationControl;
};

type CreateBillingPayload = {
  viagemIds: number[];
  totalValue?: number | null;
};

export type RejectBillingPayload = {
  id: number;
  justificationId: number;
  comment: string;
};

export type ReverseBillingPayload = {
  id: number;
  justificationId: number;
  comment: string;
};

export const useViagensBilling = () => {
  const fetchBillings = React.useCallback(async (payload: ListBillingsPayload) => {
    const url = applyQueryString('/viagens-controle/billings', {
      ...payload.filters,
      ...payload.pagination,
    });

    try {
      const { data } = await api.get<ViagensBillingPaginatedResponse>(url);
      return data;
    } catch (e) {
      throw handleApiError(e);
    }
  }, []);

  const fetchBillableViagens = React.useCallback(
    async (payload: ListBillableViagensPayload) => {
      const url = applyQueryString('/viagens-controle/billings/viagens', {
        ...payload.filters,
        ...payload.pagination,
      });

      try {
        const { data } = await api.get<ViagensBillingViagensPaginatedResponse>(
          url
        );
        return data;
      } catch (e) {
        throw handleApiError(e);
      }
    },
    []
  );

  const fetchBillingById = React.useCallback(async (id: number) => {
    try {
      const { data } = await api.get<ViagemBilling>(
        `/viagens-controle/billings/${id}`
      );
      return data;
    } catch (e) {
      throw handleApiError(e);
    }
  }, []);

  const createBilling = React.useCallback(async (payload: CreateBillingPayload) => {
    try {
      const { data } = await api.post<number>(
        '/viagens-controle/billings',
        payload
      );
      return data;
    } catch (e) {
      throw handleApiError(e);
    }
  }, []);

  const removeViagensFromBilling = React.useCallback(async (viagemIds: number[]) => {
    try {
      const { data } = await api.put('/viagens-controle/billings/remove-viagens', {
        viagemIds,
      });
      return data;
    } catch (e) {
      throw handleApiError(e);
    }
  }, []);

  const approveBilling = React.useCallback(async (id: number) => {
    try {
      const { data } = await api.post<ViagemBilling>(`/viagens-controle/billings/${id}/approve`);
      return data;
    } catch (e) {
      throw handleApiError(e);
    }
  }, []);

  const rejectBilling = React.useCallback(async ({ id, justificationId, comment }: RejectBillingPayload) => {
    try {
      const { data } = await api.post<ViagemBilling>(`/viagens-controle/billings/${id}/reject`, {
        justificationId,
        comment,
      });
      return data;
    } catch (e) {
      throw handleApiError(e);
    }
  }, []);

  const reverseBilling = React.useCallback(async ({ id, justificationId, comment }: ReverseBillingPayload) => {
    try {
      const { data } = await api.post<ViagemBilling>(`/viagens-controle/billings/${id}/reverse`, {
        justificationId,
        comment,
      });
      return data;
    } catch (e) {
      throw handleApiError(e);
    }
  }, []);

  return {
    fetchBillings,
    fetchBillableViagens,
    fetchBillingById,
    createBilling,
    removeViagensFromBilling,
    approveBilling,
    rejectBilling,
    reverseBilling,
  };
};
